import { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ICasinoGroupViewModel } from "@/fe-core/meta/interfaces/casino";
import { IFavoritesViewModel } from "@/fe-core/meta/interfaces/user";

import SVGIcon from "@/components/SVGIcon";
import {
  CASINO_DATA_SOURCE,
  CASINO_POPULAR_GAMES_DATA_SOURCE,
  CASINO_SPECIAL_GROUPS,
  LIVE_CASINO_DATA_SOURCE,
  LIVE_CASINO_POPULAR_GAMES_DATA_SOURCE,
  MAX_GAMES_PER_PAGE,
} from "@/config/casino";
import { I18N_NAMESPACE } from "@/config/i18n";
import {
  getCasinoAllGamesRequest,
  getCasinoMoreGamesGroupsRequest,
  getLiveCasinoMoreGamesGroupsRequest,
  setGameCardTooltip,
} from "@/fe-core/_redux/actions/casinoActions";
import { IRecentPlayedViewModel } from "@/fe-core/meta/interfaces/user/recentPlayed";
import { useTranslations } from "next-intl";
import Skeleton from "react-loading-skeleton";
import Select from "react-select";
import GameCard from "./GameCard";

import EmptyState from "@/components/EmptyState";
import { subVendorsSelector } from "@/fe-core/_redux/selectors/casinoVendorsSelector";
import { isDrawerOpenSelector } from "@/fe-core/_redux/selectors/drawerSelectors";
import { getGamesPlatform } from "@/fe-core/helpers/casino";
import { ICasinoVendorViewModel } from "@/fe-core/meta/interfaces/casinoVendors";
import { useScreen } from "../../context/ScreenContext";
import { usePathname } from "../../navigation";
import useTracking from "../Hooks/useTracking";
import {
  casinoGroupsMoreGamesPendingSelector,
  liveGroupsMoreGamesPendingSelector,
} from "@/fe-core/_redux/selectors/casinoSelectors";
import Image from "next/image";

type GroupPageProps = {
  groupId: string;
  group:
  | ICasinoGroupViewModel
  | IFavoritesViewModel
  | IRecentPlayedViewModel
  | undefined
  | null;
  className?: string;
  isCasinoPage?: boolean;
  isLoadingSpecialCategory?: boolean;
  isLiveCasinoPage?: boolean;
};

const GroupPage: FC<GroupPageProps> = ({
  groupId,
  group,
  className = "",
  isCasinoPage = true,
  isLoadingSpecialCategory = true,
  isLiveCasinoPage = false,
}) => {
  const vendors: ICasinoVendorViewModel[] | null =
    useSelector(subVendorsSelector);
  const dispatch = useDispatch();
  const { click, track } = useTracking();
  const pathname = usePathname() ?? "";
  const { isDesktop } = useScreen();
  const t = useTranslations(I18N_NAMESPACE.CASINO);
  const generalT = useTranslations(I18N_NAMESPACE.GENERAL);
  const gameT = useTranslations(I18N_NAMESPACE.GAME);
  const [selectedVendor, setSelectedVendor] = useState([]);
  const [selectedSort, setSelectedSort] = useState("default");
  const [selectedSortDirection, setSelectedSortDirection] = useState("ASC");
  const casinoMoreGamesLoading = useSelector(
    casinoGroupsMoreGamesPendingSelector
  );
  const liveCasinoMoreGamesLoading = useSelector(
    liveGroupsMoreGamesPendingSelector
  );

  const isDrawerOpen = useSelector(isDrawerOpenSelector);
  const isCasinoSpecialGroups = useMemo(() => {
    dispatch(setGameCardTooltip(null));
    if (CASINO_SPECIAL_GROUPS[pathname]) {
      return true;
    }
    return false;
  }, [pathname]);

  if (!group || (isCasinoSpecialGroups && isLoadingSpecialCategory)) {
    return (
      <div className="container relative mt-8 md:mt-10">
        <div className="">
          <Skeleton
            count={12}
            containerClassName={`skeleton-game-group grid grid-cols-6 gap-4`}
            inline={true}
          />
        </div>
      </div>
    );
  }

  const cardGames = () => {
    let games = group?.games.items || [];
    // if (selectedVendor.length > 0) {
    //   games = games.filter((game) =>
    //     selectedVendor.length > 0
    //       ? selectedVendor.includes(game?.vendor?.id) ||
    //         selectedVendor.includes(game?.subVendorId)
    //       : true
    //   );
    // }

    // if (selectedSort !== "default") {
    //   games = games.sort((a, b) => a.name.localeCompare(b.name));
    // }
    // switch (selectedSort) {
    //   case "default":
    //     games = games;
    //     break;
    //   case "popularity":
    //     games = games.sort((a, b) => b.popularity - a.popularity);
    //     break;
    //   case "theoreticalPayOut":
    //     games = games.sort((a, b) => b.theoreticalPayOut - a.theoreticalPayOut);
    //     break;
    //   case "name:asc":
    //     games = games.sort((a, b) => a.name.localeCompare(b.name));
    //     break;
    //   default:
    //     games = games;
    //     break;
    // }

    return games?.map((game, index) => (
      <GameCard game={game} key={`game-${index}`} hidePlayButton={true} />
    ));
  };

  const loadMoreGames = () => {
    const dataSource = isLiveCasinoPage
      ? LIVE_CASINO_DATA_SOURCE
      : CASINO_DATA_SOURCE;

    if (isLiveCasinoPage)
      dispatch(
        getLiveCasinoMoreGamesGroupsRequest({
          dataSource: dataSource,
          slug: group?.slug,
          platform: getGamesPlatform(!isDesktop),
          pagination: {
            offset: group?.games.items.length,
            limit: MAX_GAMES_PER_PAGE,
          },
        })
      );
    else {
      if (groupId === "all-games" || pathname === "/casino/all-games") {
        dispatch(
          getCasinoAllGamesRequest({
            platform: getGamesPlatform(!isDesktop),
            pagination: {
              offset: group?.games.items.length,
              limit: MAX_GAMES_PER_PAGE,
            },
          })
        );
      } else {
        dispatch(
          getCasinoMoreGamesGroupsRequest({
            dataSource: dataSource,
            slug: group?.slug,
            platform: getGamesPlatform(!isDesktop),
            pagination: {
              offset: group?.games.items.length,
              limit: MAX_GAMES_PER_PAGE,
            },
          })
        );
      }
    }

    track(`${t("load-more-cta-btn")} button clicked`, {
      buttonLabel: t("load-more-cta-btn"),
      selectedCategory: group?.name || groupId,
      selectedPage: `${isLiveCasinoPage ? "Live Casino Page" : "Casino Page"}`,
      selectedSortOption: selectedSort ? selectedSort : "null",
      selectedVendorIDFilter: selectedVendor ? selectedVendor : "null",
      component: "Casino Game List Page - GroupPage",
    });
  };

  const filterTypeSelectStyles = {
    control: (provided) => ({
      ...provided,
      padding: "6px 16px",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "4px",
      width: "260px",
      backgroundColor: "white !important",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0",
      width: "20px",
      fontWeight: "normal",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "14px !important",
      fontWeight: "400 !important",
      color: "#131416 !important",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "fit-content",
      alignSelf: "auto",
    }),
    singleValue: (provided) => ({
      ...provided,
      margin: "0",
      fontSize: "14px !important",
      fontWeight: "400 !important",
      color: "#131416 !important",
    }),
  };

  const vendorFilterTypeSelectStyles = {
    ...filterTypeSelectStyles,
    control: (provided) => ({
      ...provided,
      padding: "4px 16px",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "4px",
      width: "160px",
    }),
  };
  const sortOptions = [
    {
      value: "default",
      label: t("default"),
      id: "default",
    },
    {
      value: "name:asc",
      label: t("name"),
      id: "name",
    },
    {
      value: "popularity",
      label: t("popularity"),
      id: "popularity",
    },
    {
      value: "theoreticalPayOut",
      label: t("theoreticalPayOut"),
      id: "theoreticalPayOut",
    },
  ];

  const vendorOptions = vendors?.map((vendor) => ({
    value: vendor?.id,
    label: vendor?.identity || vendor?.displayName,
    id: vendor?.id,
  }));

  const handleVendorSelect = (params) => {
    setSelectedVendor(params?.map((option) => option.value));
    const dataSource = groupId.includes(CASINO_DATA_SOURCE)
      ? CASINO_DATA_SOURCE
      : CASINO_POPULAR_GAMES_DATA_SOURCE;
    dispatch(
      getCasinoMoreGamesGroupsRequest({
        dataSource: dataSource,
        platform: getGamesPlatform(!isDesktop),
        id: groupId,
        pagination: {
          offset: group?.games.items.length,
          limit: 2000,
        },
      })
    );
    click(`Select Vendor Filter Option`, {
      selectedSortOption: selectedSort ? selectedSort : "null",
      selectedCategory: group?.name || groupId,
      selectedVendorIDFilter: params?.map((option) => option.value),
      selectedPage: `${isLiveCasinoPage ? "Live Casino Page" : "Casino Page"}`,
      component: "Vendoroptions - GroupPage",
    });
  };

  const handleSort = (params) => {
    setSelectedSort(params.value);
    click(`Toggle Sort Option`, {
      selectedSortOption: params.value,
      selectedCategory: group?.name || groupId,
      selectedVendorFilter: selectedVendor ? selectedVendor : "null",
      selectedPage: `${isLiveCasinoPage ? "Live Casino Page" : "Casino Page"}`,
      component: "sortoptions - GroupPage",
    });

    // const dataSource = groupId.includes(CASINO_DATA_SOURCE)
    //   ? CASINO_DATA_SOURCE
    //   : CASINO_POPULAR_GAMES_DATA_SOURCE;
    // dispatch(
    //   getCasinoMoreGamesGroupsRequest({
    //     dataSource: dataSource,
    //     id: groupId,
    //     pagination: {
    //       offset: group?.games.items.length,
    //       limit: 2000,
    //     },
    //   })
    // );
  };
  const gameNavBar = () => {
    return (
      <>
        <div className="items-center justify-between md:flex relative z-[3]">
          <div className="flex items-center gap-2 p-2.5 text-sm font-normal bg-white rounded text-tertiary/70 dark:bg-tertiary-600 dark:text-white/70 w-fit mb-4 md:mb-0">
            <span className="capitalize">{gameT("slots")}</span>
            <SVGIcon
              icon={"angleArrow"}
              className="block w-3 h-3 text-tertiary/50 dark:text-white/50"
            />
            <span className="capitalize">{group.name}</span>
          </div>

          <div className="grid grid-cols-2 md:flex md:gap-6 gap-4 items-center">
            <div className="flex flex-row gap-2 justify-start items-center">
              <div className="text-sm font-normal text-tertiary/70 dark:text-white/70 whitespace-nowrap hidden md:block">
                {t("sort-by")}
              </div>
              <Select
                className="dropdown-select filter-dropdown-select select-method primary center"
                classNamePrefix="select"
                options={sortOptions}
                isSearchable={false}
                value={sortOptions.find((item) => item.value === selectedSort)}
                onChange={handleSort}
                styles={vendorFilterTypeSelectStyles}
                placeholder={t("select")}
              />
            </div>

            <div className="flex flex-row gap-2 justify-start items-center">
              <div className="text-sm font-normal text-tertiary/70 dark:text-white/70 hidden md:block">
                {t("provider")}
              </div>
              <Select
                className="dropdown-select filter-dropdown-select select-method primary center max-w-max"
                classNamePrefix="select"
                options={vendorOptions}
                isSearchable={false}
                onChange={handleVendorSelect}
                styles={filterTypeSelectStyles}
                placeholder={t("select-provider")}
                isMulti
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const gamesCardsList = cardGames();

  return (
    <div
      className={`container flex flex-col gap-4 md:gap-16 my-10 sm:mt-16 ${className}`}
    >
      {/* {isCasinoPage && !isCasinoSpecialGroups && gameNavBar()} */}

      {gamesCardsList.length === 0 ? (
        <div className="w-full flex items-center justify-center">
          <EmptyState title={t("no-games-found")} />
        </div>
      ) : (
        <div
          className={`grid grid-cols-3 gap-3 xl:grid-cols-7  ${isDrawerOpen
            ? "lg:grid-cols-4 md:grid-cols-3"
            : "lg:grid-cols-5 md:grid-cols-4"
            } `}
        >
          {gamesCardsList}
        </div>
      )}

      {casinoMoreGamesLoading || liveCasinoMoreGamesLoading ? (
        <div className="relative flex justify-center h-10">
          <Image
            src="/img/icons/loading-dots.svg"
            fill
            alt="spinner"
            className=""
            loading="lazy"
          />
        </div>
      ) : (
        gamesCardsList.length > 0 &&
        //@ts-ignore
        group?.games?.total &&
        //@ts-ignore
        gamesCardsList.length !== group?.games.total && (
          <div className="flex flex-col items-center gap-4">
            <span className="text-sm text-tertiary/50 dark:text-tertiary-50">
              {/* @ts-ignore */}
              {group?.games?.items?.length} / {group?.games?.total}
            </span>
            <button
              className="btn btn--primary rounded w-fit px-4 leading-6 py-2.5 text-sm font-bold text-tertiary"
              onClick={loadMoreGames}
            >
              {t("load-more-cta-btn")}
            </button>
          </div>
        )
      )}
    </div>
  );
};

export default GroupPage;
